/**
 * Blob Iframe Utilities
 * Creates blob URLs for isolated iframe rendering of ACH content
 */

import {
    getCachedCustomTheme,
    hexToHSL,
    darkenHex,
    lightenHex,
} from '@ai-content-hub/shared-styles';

/**
 * Generate inline CSS overrides for any active custom theme.
 * Called at blob creation time so the iframe starts with the right colors.
 */
function getCustomThemeCSS(tenantId?: string): string {
    try {
        // EAA-1664: the cache is tenant-scoped (`theme:{tenantId}`) — without
        // tenantId this read the empty legacy slot and the dev blob iframe
        // never got the tenant's theme override.
        const config = getCachedCustomTheme(tenantId);
        if (!config) return '';

        const mode =
            (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') || 'light';
        const overrides = config[mode];
        if (!overrides || Object.keys(overrides).length === 0) return '';

        const MAP: Record<string, [string, string?]> = {
            primary: ['--color-primary'],
            cta: ['--color-cta', '--primary'],
            background: ['--color-background', '--background'],
            surface: ['--color-surface', '--card'],
            text: ['--color-text', '--foreground'],
            textMuted: ['--color-text-muted', '--muted-foreground'],
            border: ['--color-border', '--border'],
            sidebarSelectedBg: ['--color-sidebar-menu-selected-bg'],
            sidebarSelectedText: ['--color-sidebar-menu-selected-text'],
            // EAA-1804: the table header is its own tenant token now, so the
            // iframe has to carry it explicitly — it used to arrive for free
            // as a derivation of `cta` below.
            tableHeaderBg: ['--color-table-header-bg'],
            tableHeaderText: ['--color-table-header-text'],
        };

        const lines: string[] = [];
        for (const [key, [hexVar, hslVar]] of Object.entries(MAP)) {
            const v = (overrides as Record<string, string>)[key];
            if (!v) continue;
            lines.push(`  ${hexVar}: ${v};`);
            if (hslVar) lines.push(`  ${hslVar}: ${hexToHSL(v)};`);
        }

        // Derived vars
        const primary = (overrides as Record<string, string>).primary;
        if (primary) {
            lines.push(`  --color-primary-hover: ${darkenHex(primary, 12)};`);
        }
        const cta = (overrides as Record<string, string>).cta;
        if (cta) {
            lines.push(`  --color-cta-hover: ${darkenHex(cta, 12)};`);
            lines.push(`  --ring: ${hexToHSL(cta)};`);
            lines.push(`  --secondary: ${hexToHSL(lightenHex(cta, 80))};`);
            lines.push(`  --secondary-foreground: ${hexToHSL(darkenHex(cta, 30))};`);
            lines.push(`  --accent: ${hexToHSL(lightenHex(cta, 85))};`);
            lines.push(`  --accent-foreground: ${hexToHSL(darkenHex(cta, 30))};`);
            lines.push(`  --primary-foreground: 0 0% 100%;`);
        }
        const tableHeaderBg = (overrides as Record<string, string>).tableHeaderBg;
        if (tableHeaderBg) {
            lines.push(`  --color-table-header-bg-hover: ${darkenHex(tableHeaderBg, 3)};`);
        }
        const background = (overrides as Record<string, string>).background;
        if (background) {
            lines.push(`  --muted: ${hexToHSL(lightenHex(background, 3))};`);
            lines.push(`  --input: ${hexToHSL(background)};`);
        }
        const surface = (overrides as Record<string, string>).surface;
        if (surface) {
            lines.push(`  --popover: ${hexToHSL(surface)};`);
        }
        const text = (overrides as Record<string, string>).text;
        if (text) {
            lines.push(`  --card-foreground: ${hexToHSL(text)};`);
            lines.push(`  --popover-foreground: ${hexToHSL(text)};`);
        }

        return lines.length > 0 ? `\n:root {\n${lines.join('\n')}\n}` : '';
    } catch {
        return '';
    }
}

// CSS Variables extracted from apps/editor/src/styles/globals.css
// These are the essential CSS variables needed for content-core components
const CSS_VARIABLES = `
:root {
  --background: 210 40% 98%;
  --foreground: 222.2 84% 4.9%;
  --card: 0 0% 100%;
  --card-foreground: 222.2 84% 4.9%;
  --popover: 0 0% 100%;
  --popover-foreground: 222.2 84% 4.9%;
  --primary: 239 84% 67%;
  --primary-foreground: 210 40% 98%;
  --secondary: 239 84% 74%;
  --secondary-foreground: 222.2 47.4% 11.2%;
  --muted: 210 40% 96.1%;
  --muted-foreground: 215.4 16.3% 46.9%;
  --accent: 210 40% 96.1%;
  --accent-foreground: 222.2 47.4% 11.2%;
  --destructive: 0 84.2% 60.2%;
  --destructive-foreground: 210 40% 98%;
  --success: 160 84% 39%;
  --success-foreground: 210 40% 98%;
  --warning: 38 92% 50%;
  --warning-foreground: 222.2 47.4% 11.2%;
  --border: 214.3 31.8% 91.4%;
  --input: 214.3 31.8% 91.4%;
  --ring: 239 84% 67%;
  --radius: 0.75rem;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  height: 100%;
}

body {
  font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background: hsl(var(--background));
  color: hsl(var(--foreground));
  line-height: 1.5;
  height: 100%;
  margin: 0;
  overflow: hidden;
}

.ach-iframe-scroll {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

#root {
  min-height: 100%;
}
`;

// Message types for parent-iframe communication
// Editor messages use EDITOR_ prefix, Player messages use PLAYER_ prefix
export type IframeMessageType =
  // Editor-specific messages
  | 'EDITOR_INIT'
  | 'EDITOR_READY'
  | 'EDITOR_CONTENT_CHANGE'
  | 'EDITOR_HEIGHT_CHANGE'
  | 'EDITOR_GET_CONTENT'
  | 'EDITOR_CONTENT_DATA'
  | 'EDITOR_SAVE'
  | 'EDITOR_CONFIG_UPDATE'
  | 'EDITOR_ERROR'
  | 'EDITOR_CONTENT_SAVED'
  | 'EDITOR_SAVE_REQUESTED'
  | 'EDITOR_CONTENT_DELETED'
  | 'EDITOR_CONTENT_ERROR'
  // Player-specific messages
  | 'PLAYER_INIT'
  | 'PLAYER_READY'
  | 'PLAYER_HEIGHT_CHANGE'
  | 'PLAYER_ANSWER'
  | 'PLAYER_SUBMIT'
  | 'PLAYER_COMPLETE'
  | 'PLAYER_CONFIG_UPDATE'
  | 'PLAYER_RESET'
  // Content CRUD message types (compatible with content-api iframe-bridge)
  | 'CONTENT_GET'
  | 'CONTENT_SAVE'
  | 'CONTENT_UPDATE'
  | 'CONTENT_SAVED'
  | 'CONTENT_DELETE'
  | 'CONTENT_DELETED'
  | 'CONTENT_ERROR';

export interface IframeMessage<T = unknown> {
  type: IframeMessageType;
  contentId: string;
  payload?: T;
  requestId?: string;
  timestamp: number;
}

/**
 * Generate the base HTML for blob iframe
 */
function generateBaseHtml(contentId: string, mode: 'editor' | 'player', tenantId?: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ACH ${mode === 'editor' ? 'Editor' : 'Player'}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  <style>${CSS_VARIABLES}${getCustomThemeCSS(tenantId)}</style>
</head>
<body>
  <div class="ach-iframe-scroll">
    <div id="root">
      <div style="padding: 24px; text-align: center; color: hsl(var(--muted-foreground));">
        Loading ${mode}...
      </div>
    </div>
  </div>
  <script>
    (function() {
      const contentId = '${contentId}';
      const mode = '${mode}';
      let currentContent = null;
      let currentConfig = null;
      
      // Get mode-specific message prefix
      const msgPrefix = mode === 'editor' ? 'EDITOR' : 'PLAYER';
      
      // Send message to parent
      function sendToParent(type, payload) {
        window.parent.postMessage({
          type: type,
          contentId: contentId,
          payload: payload,
          timestamp: Date.now()
        }, '*');
      }
      
      // Report height changes
      function reportHeight() {
        const height = document.body.scrollHeight;
        sendToParent(msgPrefix + '_HEIGHT_CHANGE', { height: height });
      }
      
      // Handle messages from parent
      window.addEventListener('message', function(event) {
        const message = event.data;
        if (!message || message.contentId !== contentId) return;
        
        switch (message.type) {
          case msgPrefix + '_INIT':
            currentContent = message.payload?.content;
            currentConfig = message.payload?.config;
            renderContent();
            break;
          case msgPrefix + '_GET_CONTENT':
            sendToParent(msgPrefix + '_CONTENT_DATA', { content: currentContent });
            break;
        }
      });
      
      // Render content (placeholder - actual rendering requires React bundle)
      function renderContent() {
        const root = document.getElementById('root');
        if (!currentContent) {
          root.innerHTML = '<div style="padding: 24px; text-align: center;">No content provided</div>';
          return;
        }
        
        // Display content info (actual React rendering requires bundle from URL)
        root.innerHTML = \`
          <div style="padding: 16px; border: 1px solid hsl(var(--border)); border-radius: 8px;">
            <div style="font-weight: 600; margin-bottom: 8px;">Content: \${currentContent.type}</div>
            <div style="color: hsl(var(--muted-foreground)); font-size: 14px;">
              ID: \${currentContent.id || 'N/A'}
            </div>
            <div style="margin-top: 16px; padding: 12px; background: hsl(var(--muted)); border-radius: 4px; font-size: 13px;">
              Note: For full \${mode} functionality, set VITE_ACH_EMBED_URL to use the content-studio app.
            </div>
          </div>
        \`;
        
        setTimeout(reportHeight, 100);
      }
      
      // Observe body size changes
      const resizeObserver = new ResizeObserver(function() {
        reportHeight();
      });
      resizeObserver.observe(document.body);
      
      // Signal ready
      sendToParent(msgPrefix + '_READY', null);
    })();
  </script>
</body>
</html>`;
}

/**
 * Create blob URL for editor iframe
 */
export function createEditorBlobUrl(contentId: string, tenantId?: string): string {
  const html = generateBaseHtml(contentId, 'editor', tenantId);
  const blob = new Blob([html], { type: 'text/html' });
  return URL.createObjectURL(blob);
}

/**
 * Create blob URL for player iframe
 */
export function createPlayerBlobUrl(contentId: string, tenantId?: string): string {
  const html = generateBaseHtml(contentId, 'player', tenantId);
  const blob = new Blob([html], { type: 'text/html' });
  return URL.createObjectURL(blob);
}

/**
 * Revoke blob URL to free memory
 */
export function revokeBlobUrl(url: string): void {
  if (url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}
